/**
 * DraftEntriesDashboard Component
 * View, resume, and delete draft production entries
 */

import React, { useState, useEffect } from 'react';
import { draftEntryService } from '../../utils/services/draftEntryService';
import { authService } from '../../utils/auth/authService';
import type { DraftProductionEntry } from '../../types/draftEntry';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { toast } from 'sonner';
import { Clock, Trash2, PlayCircle, AlertCircle } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';

export interface DraftEntriesDashboardProps {
  userId?: string; // Optional - will use current user from auth if not provided
  onResumeDraft?: (draft: DraftProductionEntry) => void;
}

export const DraftEntriesDashboard: React.FC<DraftEntriesDashboardProps> = ({
  userId: providedUserId,
  onResumeDraft,
}) => {
  const [drafts, setDrafts] = useState<DraftProductionEntry[]>([]);
  const [loading, setLoading] = useState(true);

  // Get userId from props or auth context
  const currentUser = authService.getCurrentUser();
  const userId = providedUserId || currentUser?.id || 'anonymous';

  useEffect(() => {
    loadDrafts();
  }, [userId]);

  const loadDrafts = async () => {
    try {
      setLoading(true);
      const allDrafts = await draftEntryService.getAllDrafts(userId);
      setDrafts(allDrafts);
    } catch (error) {
      console.error('Failed to load drafts:', error);
      toast.error('Failed to load draft entries');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDraft = async (draftId: string, wellName: string) => {
    if (!confirm(`Delete draft for ${wellName}? This cannot be undone.`)) {
      return;
    }

    try {
      await draftEntryService.deleteDraft(draftId);
      setDrafts(prev => prev.filter(d => d.id !== draftId));
      toast.success('Draft deleted');
    } catch (error) {
      console.error('Failed to delete draft:', error);
      toast.error('Failed to delete draft');
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-gray-900">In-Progress Entries</h2>
        <p className="text-gray-600">Loading drafts...</p>
      </div>
    );
  }

  if (drafts.length === 0) {
    return (
      <div className="space-y-4">
        <h2 className="text-2xl font-bold text-gray-900">In-Progress Entries</h2>
        <Card>
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No draft entries found</p>
            <p className="text-sm text-gray-500 mt-2">
              Start a new production entry to see it here
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">In-Progress Entries</h2>
        <p className="text-sm text-gray-600">
          {drafts.length} {drafts.length === 1 ? 'draft' : 'drafts'}
        </p>
      </div>

      <div className="space-y-3">
        {drafts.map(draft => (
          <Card key={draft.id} className="hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                {/* Draft info */}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-lg text-gray-900">
                      {draft.wellName}
                    </h3>
                    {draft.isComplete && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                        Complete
                      </span>
                    )}
                  </div>
                  
                  <div className="space-y-1 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <Clock className="h-4 w-4" />
                      <span>
                        {format(new Date(draft.entryDate), 'MMMM d, yyyy')} at {draft.timeLabel}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500">
                      Last modified {formatDistanceToNow(new Date(draft.lastModifiedAt), { addSuffix: true })}
                    </p>
                  </div>

                  {/* Progress */}
                  <div className="mt-3 space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-gray-700">
                        {draft.completionPercentage}% complete
                      </span>
                      <span className="text-gray-500">
                        {Object.values(draft.fields).filter(f => f.isSaved).length} fields saved
                      </span>
                    </div>
                    <Progress value={draft.completionPercentage} className="h-2" />
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-row md:flex-col gap-2 md:items-end">
                  <Button
                    size="sm"
                    onClick={() => onResumeDraft?.(draft)}
                    className="gap-2 flex-1 md:flex-none"
                  >
                    <PlayCircle className="h-4 w-4" />
                    Resume
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDeleteDraft(draft.id, draft.wellName)}
                    className="gap-2 flex-1 md:flex-none"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Info message */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <p className="text-sm text-blue-900">
            <strong>💡 Tip:</strong> Drafts are automatically saved as you type. 
            You can close the form anytime and resume later from this dashboard.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
